/**
 * Undo by opponent consent (owner decision Q47; TR 509).
 *
 * Every state-changing table command records a snapshot of the state before it. A player may ask to
 * undo their own most recent steps, as long as nobody acted after them (TR 509.3); every other player
 * must accept. Steps that used randomness or showed the actor hidden cards (draw, shuffle, look at,
 * reveal, taking a card out of a deck) are **barriers**: they clear the history, because undoing them
 * would let a player fish for a better outcome (TR 509.7). A turn ending is a barrier too.
 *
 * Restoring a snapshot keeps the current RNG state (redoing a shuffle gives a new order), the logical
 * clock (ids and timestamps never repeat) and `seq` (it keeps increasing for clients).
 *
 * Tier 2 can record steps as `free` (its automatic bookkeeping): those are undone at once without
 * consent (Q47).
 */
import { type Rejection, reject } from '../../kernel/errors';
import { deepCloneJson } from '../../kernel/json';
import { opponentsOf } from '../core/queries';
import type { GameState, UndoEntry, UndoSnapshot } from '../state/game-state';
import type { PlayerId } from '../state/ids';
import { type CommandContext, emit } from './context';
import type { TableCommand, TableCommandType } from './types';

/** How many undoable steps are kept (the oldest is dropped beyond this). */
export const UNDO_DEPTH = 8;

/** Commands that never enter the undo history (they change no game state, or manage undo itself). */
const NOT_RECORDED: ReadonlySet<TableCommandType> = new Set<TableCommandType>([
  'pointAt',
  'undoRequest',
  'undoAnswer',
  'concede',
  'decide',
]);

/** A state without its undo history (what a snapshot holds). Shares structure with `state`. */
export function snapshotOf(state: GameState): UndoSnapshot {
  const { undo: _undo, ...rest } = state;
  return rest;
}

/**
 * Updates the draft's undo history after a successful table command: records the step, or clears the
 * history at a barrier. Any recorded step also withdraws an open request (play moved on).
 */
export function recordUndoStep(
  before: GameState,
  draft: GameState,
  by: PlayerId,
  cmd: TableCommand,
  barrier: boolean,
  free = false,
): void {
  if (NOT_RECORDED.has(cmd.t)) return;
  recordUndo(before, draft, by, cmd.t, { barrier, free });
}

/**
 * Records one undoable step labelled `command` (a table command type, or a higher layer's label such
 * as `recommended` or `auto:heal`), or clears the history at a barrier. Higher layers use it for their
 * own steps; tier 2 records its automatic bookkeeping with `free: true` (Q47).
 */
export function recordUndo(
  before: GameState,
  draft: GameState,
  by: PlayerId,
  command: string,
  options: { barrier: boolean; free?: boolean },
): void {
  draft.undo.request = null;
  if (options.barrier || draft.result !== null) {
    draft.undo.history = [];
    return;
  }
  const entry: UndoEntry = { seq: draft.seq, by, command, free: options.free ?? false, before: snapshotOf(before) };
  draft.undo.history = [...draft.undo.history, entry].slice(-UNDO_DEPTH);
}

/** How many of `player`'s most recent steps are undoable (trailing steps by that player). */
export function undoableCount(state: GameState, player: PlayerId): number {
  let n = 0;
  for (let i = state.undo.history.length - 1; i >= 0; i--) {
    if (state.undo.history[i]?.by !== player) break;
    n++;
  }
  return n;
}

/** Restores the state from before the last `count` steps (consent already given). */
function performUndo(ctx: CommandContext, by: PlayerId, count: number): void {
  const draft = ctx.draft;
  const history = draft.undo.history;
  const target = history[history.length - count];
  if (!target) return;
  const { rng, clock, seq } = draft;
  const restored = deepCloneJson(target.before);
  Object.assign(draft, restored, {
    rng,
    clock,
    seq,
    undo: { history: history.slice(0, history.length - count), request: null },
  });
  emit(ctx, { type: 'undone', actor: null, by, count });
}

type UndoRequestCmd = Extract<TableCommand, { t: 'undoRequest' }>;
type UndoAnswerCmd = Extract<TableCommand, { t: 'undoAnswer' }>;

/** Asks to undo the actor's last steps (undone at once when no consent is needed). */
export function handleUndoRequest(ctx: CommandContext, cmd: UndoRequestCmd): Rejection | undefined {
  const draft = ctx.draft;
  if (draft.undo.request !== null) return reject('undoPending');
  const steps = cmd.steps ?? 1;
  if (!Number.isInteger(steps) || steps < 1 || steps > UNDO_DEPTH)
    return reject('outOfRange', `steps must be 1-${UNDO_DEPTH}`);
  if (steps > undoableCount(draft, ctx.actor)) return reject('undoUnavailable');
  const entries = draft.undo.history.slice(-steps);
  const awaiting = opponentsOf(draft, ctx.actor);
  emit(ctx, { type: 'undoRequested', actor: ctx.actor, count: steps });
  if (awaiting.length === 0 || entries.every((e) => e.free)) {
    performUndo(ctx, ctx.actor, steps);
    return undefined;
  }
  draft.undo.request = { by: ctx.actor, count: steps, seq: draft.seq, awaiting };
  return undefined;
}

/** Accepts or declines the open undo request. */
export function handleUndoAnswer(ctx: CommandContext, cmd: UndoAnswerCmd): Rejection | undefined {
  const draft = ctx.draft;
  const request = draft.undo.request;
  if (request === null) return reject('noUndoRequest');
  if (!request.awaiting.includes(ctx.actor)) return reject('notPermitted', 'The request is not waiting for you');
  emit(ctx, { type: 'undoAnswered', actor: ctx.actor, accept: cmd.accept });
  if (!cmd.accept) {
    draft.undo.request = null;
    return undefined;
  }
  const awaiting = request.awaiting.filter((p) => p !== ctx.actor);
  if (awaiting.length > 0) {
    draft.undo.request = { ...request, awaiting };
    return undefined;
  }
  performUndo(ctx, request.by, request.count);
  return undefined;
}
